import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DecadesTimeline, { decades } from '../components/DecadesTimeline';

// Hook to detect when element enters viewport
const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.2, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const HistoryPage = () => {
  const [activeEra, setActiveEra] = useState(4);
  const [headerRef, headerVisible] = useInView();
  const [contentRef, contentVisible] = useInView();
  const [valuesRef, valuesVisible] = useInView();

  return (
    <main className="bg-[#0a0a0a] min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">Our Legacy</p>
          <h1 className="text-[48px] md:text-[64px] font-extralight text-white mb-6 tracking-[-0.02em]">
            LV1 Throughout the Decades
          </h1>
          <p className="text-[#8a8a8a] text-[17px] leading-[1.7] max-w-[600px] mx-auto">
            A record of our work, told through the decades of building brands, fostering partnerships, and delivering product at scale.
          </p>
        </div>

        {/* Timeline Navigation - using shared component */}
        <div className="relative max-w-4xl mx-auto mb-16">
          <DecadesTimeline
            variant="full"
            nodeSize={64}
            initialActive={activeEra}
            onDecadeChange={setActiveEra}
          />
        </div>

        {/* Active Era Content */}
        <div
          ref={contentRef}
          key={activeEra}
          className="max-w-4xl mx-auto p-10 rounded-2xl mb-16"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
            animation: 'content-fade-in 0.4s ease-out',
          }}
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="text-[48px] font-extralight text-white/20">
              {decades[activeEra].year}
            </span>
            <div>
              <h2 className="text-white text-[28px] font-light">{decades[activeEra].title}</h2>
              <p className="text-[#6a6a6a] text-[14px]">{decades[activeEra].era}</p>
            </div>
          </div>

          <p className="text-[#8a8a8a] text-[16px] leading-[1.8] mb-8">
            {decades[activeEra].description}
          </p>

          {/* Highlights */}
          <div className="space-y-3">
            <p className="text-white/60 text-[13px] uppercase tracking-wider mb-4">Key Milestones</p>
            {decades[activeEra].highlights.map((highlight, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.03]"
                style={{
                  animation: `highlight-fade-in 0.4s ease-out ${index * 0.08}s both`,
                }}
              >
                <div className="w-2 h-2 rounded-full bg-white/30" />
                <p className="text-white/80 text-[15px]">{highlight}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Family Values Section */}
        <div
          ref={valuesRef}
          className="text-center max-w-3xl mx-auto"
          style={{
            opacity: valuesVisible ? 1 : 0,
            transform: valuesVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <h3 className="text-white text-[28px] font-light mb-6">Built on Family Values</h3>
          <p className="text-[#8a8a8a] text-[16px] leading-[1.8] mb-10">
            Through every decade, LV1 has remained a family-led business. Our approach to partnerships, our commitment to quality, and our long-term perspective all stem from the values established when we first started. We believe relationships matter, and that belief has guided every decision we have made.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center px-7 py-3.5 border border-white/20 text-white rounded-full text-[15px] font-medium hover:bg-white/5 hover:border-white/30 transition-all duration-300"
          >
            Learn More About Us
          </Link>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes content-fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes highlight-fade-in {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </main>
  );
};

export default HistoryPage;
